<template>
  <FormSection
    :icon="IconColoredLocation"
    :title="capitalizeTranslation('names.inventory')">
    <template #pane-header-right>
      <div
        v-if="areUnitsLoading"
        class="unit-loader badge badge-rounded badge-sm badge-info ml-5">
        <loader-small />
        <div class="mr-3">{{ capitalizeTranslation('messages.updatingMap') }}...</div>
      </div>
    </template>
    <template #actions>
      <label
        v-show="isViewSummaryVisible"
        id="view-summary"
        for="summary-filter">
        <span class="checkbox-container">
          <input
            id="summary-filter"
            v-model="isSummaryTargetingEnabled"
            type="checkbox" />
          <span class="checkmark" />
        </span>
        <span class="group-label">{{ capitalizeTranslation('actions.viewSummary') }}</span>
      </label>
      <button
        class="btn btn-default btn-sm mr-3"
        @click="openFullScreenMap">
        <icon-full-screen />
        {{ capitalizeTranslation('messages.fullscreen') }}
      </button>
    </template>
  </FormSection>
  <!-- PG WORKFLOW -->
  <transition name="squish fade">
    <div
      v-if="isMounted"
      v-show="isFlexible && !isPreferredOrNGInternalDeal"
      class="form-section-content mb-0">
      <div class="form-group row">
        <div class="col-sm-12">
          <div class="flex-h">
            <span class="flex-grow ml-3">{{ capitalizeTranslation('messages.allowBuyersToChooseLocations') }}</span>
            <ToggleButton
              class="mb-0"
              :color="Colors.success"
              :value="locationsOverridable"
              :labels="{
                checked: capitalizeTranslation('actions.yes'),
                unchecked: capitalizeTranslation('actions.no'),
              }"
              @change="toggleLocations()" />
          </div>
        </div>
      </div>
      <div class="form-group row">
        <div class="col-sm-12">
          <div class="flex-h">
            <span class="flex-grow ml-3">{{ capitalizeTranslation('messages.allowBuyersToChooseNetworks') }}</span>
            <ToggleButton
              id="predefined_networks_toggle"
              class="mb-0"
              :color="Colors.success"
              :value="networksOverridable"
              :labels="{
                checked: capitalizeTranslation('actions.yes'),
                unchecked: capitalizeTranslation('actions.no'),
              }"
              @change="toggleNetworks()" />
          </div>
        </div>
      </div>
      <div class="form-group row">
        <div class="col-sm-12">
          <div class="flex-h">
            <span class="flex-grow ml-3">{{ capitalizeTranslation('messages.allowBuyersToChooseVenueTypes') }}</span>
            <ToggleButton
              class="mb-0"
              :color="Colors.success"
              :value="mediaTypesOverridable"
              :labels="{
                checked: capitalizeTranslation('actions.yes'),
                unchecked: capitalizeTranslation('actions.no'),
              }"
              @change="toggleMediaTypes()" />
          </div>
        </div>
      </div>
      <div
        v-if="organizationSettings.uiEnableLineItemUnitTargeting"
        class="form-group row">
        <div class="col-sm-12">
          <div class="flex-h">
            <span class="flex-grow ml-3">{{ capitalizeTranslation('messages.allowBuyersToChooseScreens') }}</span>
            <ToggleButton
              class="mb-0"
              :color="Colors.success"
              :value="unitAssignmentsOverridable"
              :labels="{
                checked: capitalizeTranslation('actions.yes'),
                unchecked: capitalizeTranslation('actions.no'),
              }"
              @change="toggleUnitAssignments()" />
          </div>
        </div>
      </div>
    </div>
  </transition>
  <!-- OVERALL MAP -->
  <transition name="squish fade">
    <div
      v-if="isMounted"
      ref="locationSelectorRef"
      class="location-selector"
      :class="{ 'full-screen-map': shouldDisplayFullScreenMap }">
      <OverallMap
        v-if="isReady"
        ref="overallMapRef"
        :line-item="deal"
        :suppliers="mapSuppliers"
        :enable-scroll-zoom="false"
        :fit-bounds-right-padding="shouldDisplayFullScreenMap ? 370 : 64"
        :are-units-read-only="unitAssignmentsOverridable && !isPreferredOrNGInternalDeal"
        :are-sites-read-only="locationsOverridable && !isPreferredOrNGInternalDeal"
        :are-networks-read-only="networksOverridable && !isPreferredOrNGInternalDeal"
        :are-proximity-geofence-groups-read-only="locationsOverridable && !isPreferredOrNGInternalDeal"
        :are-media-types-read-only="mediaTypesOverridable && !isPreferredOrNGInternalDeal"
        :are-markets-read-only="locationsOverridable && !isPreferredOrNGInternalDeal"
        :are-locations-read-only="locationsOverridable && !isPreferredOrNGInternalDeal"
        :display-unit-pack-filter="true"
        :load-all-options-for-filters="true"
        :enable-targeting-summary="isSummaryTargetingEnabled && !shouldDisplayFullScreenMap"
        :is-editing="true"
        @has-started-filtering="(val: boolean) => (hasStartedFiltering = val)"
        @show-view-summary="(val: boolean) => (isViewSummaryVisible = val)"
        @loaded="isOverallMapReady = true"
        @units-loading="() => (areUnitsLoading = true)"
        @units-loaded="() => (areUnitsLoading = false)"
        @cart-changed="onOverallMapSelectionsChanged"
        @proximity-geofences-changed="(geofences) => emits('onProximityGeofencesChanged', geofences)"
        @filter-drop-down-hidden="onFilterDropDownHidden"
        @filter-drop-down-visible="onFilterDropDownVisible" />
      <button
        id="exit_fullscreenmapInternal"
        class="btn btn-default btn-sm"
        @click="exitFullscreenMap">
        <icon-exit-full-screen />
        {{ i('actions.exitFullscreen') }}
      </button>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, Ref, ref, useTemplateRef } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';

  import Colors from '@/Colors';
  import FormSection from '@/common/components/controls/FormSection.vue';
  import LoaderSmall from '@/common/components/controls/LoaderSmall.vue';
  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import { IconColoredLocation } from '@/common/icons/colored';
  import { IconExitFullScreen, IconFullScreen } from '@/common/icons/monochrome';
  import { Package } from '@/common/models';
  import { capitalizeTranslation, i, useInjected } from '@/globals';
  import { OverallMap } from '@/org-app/components/inventory';
  import MapCart from '@/org-app/components/inventory/overallMap/MapCart';
  import { useDealComputedProperties } from '@/org-app/services/computedProperties/DealComputedProperties';
  import { mapOrgAppState } from '@/utils/StateMapping';

  const { organizationSettings } = mapOrgAppState();

  interface IProps {
    isFlexible: boolean;
    isPreferredOrNGInternalDeal: boolean;
    isReady: boolean;
    mapSuppliers: Array<any>;
  }

  const { isFlexible, isPreferredOrNGInternalDeal, isReady, mapSuppliers } = defineProps<IProps>();

  const emits = defineEmits(['onOverallMapSelectionsChanged', 'onProximityGeofencesChanged']);

  //#region Data

  const deal: Ref<Package> = useInjected<Package>('deal');

  const areUnitsLoading: Ref<boolean> = ref(false);
  const isViewSummaryVisible: Ref<boolean> = ref(false);
  const isSummaryTargetingEnabled: Ref<boolean> = ref(true);
  const shouldDisplayFullScreenMap: Ref<boolean> = ref(false);
  const isMounted: Ref<boolean> = ref(false);
  const hasStartedFiltering: Ref<boolean> = ref(false);
  const isOverallMapReady = ref(false);
  const isEstimatedDeliveryStatsVisible = ref(false);
  const overallMapTargetingKey = ref(0);

  let onOverallMapSelectionsChangedTimer = null;

  //#endregion

  //#region Computed Properties

  const {
    locationsOverridable,
    networksOverridable,
    mediaTypesOverridable,
    languageExclusionIDs,
    languageIDs,
    locationIDs,
    locationExclusionIDs,
    siteIDs,
    siteExclusionIDs,
    unitIDs,
    unitExclusionIDs,
    unitPackIDs,
    unitPackExclusionIDs,
    geofenceGroupIDs,
    geofenceGroupExclusionIDs,
    marketIDs,
    mediaTypeIDs,
    networkIDs,
    creativeFormats,
    creativeFormatExclusions,
    creativeOrientations,
    creativeOrientationExclusions,
    iabCategories,
    iabCategoryExclusions,
    unitAssignmentsOverridable,
  } = useDealComputedProperties(deal, {
    locationsOverridable: (value: boolean) => setLocationsOverridable(value),
    mediaTypesOverridable: (value: boolean) => setMediaTypesOverridable(value),
    networksOverridable: (value: boolean) => setNetworksOverridable(value),
    unitAssignmentsOverridable: (value: boolean) => setUnitAssignmentsOverridable(value),
  });

  //#endregion

  //#region DOM Refs

  const overallMapRef = useTemplateRef<ComponentExposed<typeof OverallMap>>('overallMapRef');
  const locationSelectorRef = ref(null);

  //#endregion

  //#region Methods

  function onOverallMapSelectionsChanged(cart: MapCart) {
    overallMapTargetingKey.value += 1;
    if (onOverallMapSelectionsChangedTimer != null) {
      clearTimeout(onOverallMapSelectionsChangedTimer);
    }
    overallMapTargetingKey.value += 1;
    onOverallMapSelectionsChangedTimer = setTimeout(() => {
      onOverallMapSelectionsChangedTimer = null;
      emits('onOverallMapSelectionsChanged', overallMapTargetingKey.value);
    }, 200);

    languageExclusionIDs.value = cart.excludedLanguageIds;
    languageIDs.value = cart.includedLanguageIds;
    locationIDs.value = cart.includedLocationIDs;
    locationExclusionIDs.value = cart.excludedLocationIDs;
    siteIDs.value = cart.includedSiteIDs;
    siteExclusionIDs.value = cart.excludedSiteIDs;
    unitIDs.value = cart.includedUnitIDs;
    unitExclusionIDs.value = cart.excludedUnitIDs;
    unitPackIDs.value = cart.includedUnitPackIDs;
    unitPackExclusionIDs.value = cart.excludedUnitPackIDs;
    geofenceGroupIDs.value = cart.includedGeofenceGroupIDs;
    geofenceGroupExclusionIDs.value = cart.excludedGeofenceGroupIDs;
    marketIDs.value = cart.includedMarketIDs;
    mediaTypeIDs.value = cart.includedMediaTypeIDs;
    networkIDs.value = cart.includedNetworkIDs;
    creativeFormats.value = cart.includedCreativeFormatsIDs;
    creativeFormatExclusions.value = cart.excludedCreativeFormatsIDs;
    creativeOrientations.value = cart.includedCreativeOrientationIDs;
    creativeOrientationExclusions.value = cart.excludedCreativeOrientationIDs;
    iabCategories.value = cart.includedIabCategoryIDs;
    iabCategoryExclusions.value = cart.excludedIabCategoryIDs;
  }

  function openFullScreenMap() {
    shouldDisplayFullScreenMap.value = true;
    overallMapRef.value?.enableMouseWheel();
    let count = 10;
    const timer = setInterval(() => {
      overallMapRef.value?.map.resize();
      count -= 1;
      if (count === 0) {
        clearInterval(timer);
      }
    }, 100);
  }

  function exitFullscreenMap() {
    shouldDisplayFullScreenMap.value = false;
    overallMapRef.value.disableMouseWheel();
  }

  function onFilterDropDownHidden() {
    isEstimatedDeliveryStatsVisible.value = true;
  }

  function onFilterDropDownVisible() {
    if (shouldDisplayFullScreenMap.value) {
      isEstimatedDeliveryStatsVisible.value = false;
    }
  }

  function getFilteredLocations() {
    return overallMapRef.value?.getFilteredLocations();
  }

  function initCartFromEntity(entity: Package, updateSelectedFilters: boolean = true) {
    overallMapRef.value?.initCartFromEntity(entity, updateSelectedFilters);
  }

  function clearFilters() {
    overallMapRef.value?.clearFilters();
  }

  function toggleLocations() {
    locationsOverridable.value = !locationsOverridable.value;
  }

  function toggleNetworks() {
    networksOverridable.value = !networksOverridable.value;
  }

  function toggleMediaTypes() {
    mediaTypesOverridable.value = !mediaTypesOverridable.value;
  }

  function toggleUnitAssignments() {
    unitAssignmentsOverridable.value = !unitAssignmentsOverridable.value;
  }

  function setOverridableFields(value: boolean) {
    unitAssignmentsOverridable.value = value;
    locationsOverridable.value = value;
    networksOverridable.value = value;
    mediaTypesOverridable.value = value;
  }

  //#endregion

  //#region Setter Mutations

  function setLocationsOverridable(value: boolean) {
    if (value) {
      marketIDs.value = [];
      siteIDs.value = [];
      siteExclusionIDs.value = [];
      unitIDs.value = [];
      unitExclusionIDs.value = [];
      unitPackIDs.value = [];
      unitPackExclusionIDs.value = [];
      locationIDs.value = [];
      locationExclusionIDs.value = [];
      geofenceGroupIDs.value = [];
      geofenceGroupExclusionIDs.value = [];
      creativeFormats.value = [];
      creativeFormatExclusions.value = [];
      initCartFromEntity(deal.value, false);
    }
  }

  function setMediaTypesOverridable(value: boolean) {
    if (value) {
      mediaTypeIDs.value = [];
      initCartFromEntity(deal.value, false);
    }
  }

  function setNetworksOverridable(value: boolean) {
    if (value) {
      networkIDs.value = [];
      initCartFromEntity(deal.value, false);
    }
  }

  function setUnitAssignmentsOverridable(value: boolean) {
    if (value) {
      unitIDs.value = [];
      unitExclusionIDs.value = [];
      initCartFromEntity(deal.value, false);
    }
  }

  //#endregion

  //#region Lifecycle Hooks

  onMounted(() => {
    isMounted.value = true;
  });

  onBeforeUnmount(() => {
    isMounted.value = false;
  });

  defineExpose({
    overallMapRef,
    shouldDisplayFullScreenMap,
    getFilteredLocations,
    initCartFromEntity,
    clearFilters,
    setOverridableFields,
  });

  //#endregion
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;
</style>
